// Iter AX — persistent "Getting started" checklist. Items self-check from real
// data; stays on the dashboard until everything is done (or dismissed).
import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, X, RotateCcw } from "lucide-react";

export const GettingStartedCard = ({ clients, onAddClient, onReplayTour }) => {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("pstr_gs_dismissed") === "1");

  const hasClient = clients.length > 0;
  const hasSession = clients.some((c) => (c.session_count || 0) > 0);
  const visitedSettings = localStorage.getItem("pstr_visited_settings") === "1";
  const hasPasskey = localStorage.getItem("pstr_passkey") === "1";

  const items = [
    { key: "client", label: "Add your first client", done: hasClient, action: onAddClient, actionLabel: "Add client" },
    { key: "session", label: "Complete your first session", done: hasSession, to: hasClient ? `/clients/${clients[0].id}` : null, actionLabel: "Open a client" },
    { key: "settings", label: "Set your clinic details & preferences", done: visitedSettings, to: "/settings", actionLabel: "Open Settings" },
    { key: "biometrics", label: "Enable biometric sign-in (optional)", done: hasPasskey, to: "/settings", actionLabel: "Enable" },
  ];
  const doneCount = items.filter((i) => i.done).length;
  if (dismissed || doneCount === items.length) return null;

  const dismiss = () => { localStorage.setItem("pstr_gs_dismissed", "1"); setDismissed(true); };

  return (
    <div className="bg-white border border-paper-200 rounded-3xl p-6 mb-8" data-testid="getting-started-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="font-heading text-lg text-ink-900">Getting started</div>
          <div className="text-xs text-ink-500 mt-0.5">{doneCount} of {items.length} done</div>
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="replay-tour-btn" onClick={onReplayTour} title="Replay the welcome tour" className="min-h-[36px] px-3 rounded-full hover:bg-paper-100 text-ink-500 hover:text-ink-900 text-xs inline-flex items-center gap-1.5">
            <RotateCcw className="w-3.5 h-3.5" strokeWidth={2} /> Replay tour
          </button>
          <button data-testid="dismiss-getting-started" onClick={dismiss} className="w-8 h-8 rounded-full hover:bg-paper-100 flex items-center justify-center text-ink-500">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-paper-100 mb-4 overflow-hidden">
        <div className="h-full bg-sage-500 rounded-full transition-all duration-500" style={{ width: `${(doneCount / items.length) * 100}%` }} />
      </div>
      <ul className="space-y-2.5">
        {items.map((i) => (
          <li key={i.key} className="flex items-center gap-3" data-testid={`gs-item-${i.key}`}>
            {i.done
              ? <CheckCircle2 className="w-5 h-5 text-sage-600 shrink-0" strokeWidth={1.5} />
              : <Circle className="w-5 h-5 text-paper-200 shrink-0" strokeWidth={1.5} />}
            <span className={`flex-1 text-sm ${i.done ? "text-ink-500 line-through" : "text-ink-900"}`}>{i.label}</span>
            {!i.done && (i.action ? (
              <button onClick={i.action} data-testid={`gs-action-${i.key}`} className="text-xs font-medium text-sage-700 hover:text-sage-600 underline underline-offset-4">{i.actionLabel}</button>
            ) : i.to ? (
              <Link to={i.to} data-testid={`gs-action-${i.key}`} className="text-xs font-medium text-sage-700 hover:text-sage-600 underline underline-offset-4">{i.actionLabel}</Link>
            ) : null)}
          </li>
        ))}
      </ul>
    </div>
  );
};
